import { InstrumentTypeCode } from "@/lib/interfaces and Enums/ENUMInstrumentTypeCode";
import { QuoteSide } from "@/lib/interfaces and Enums/ENUMQuoteSide";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { ExecutionRequestQuoteLeg } from "@/lib/interfaces and Enums/InterfaceExecutionRequestQuoteLeg";
import agGridFlashNotifications from "@/store/notifications/agGridFlashNotifications";
import tradeDataStore from "@/store/tradeDataStore";
import tradeSettingsStore, {
  ILegCellToFlash,
} from "@/store/tradeSettingsStore";
import { isNumericallyZero } from "@/lib/businessRules/zeroNormalizationHelper";

// Sentinel blockId for ER-level flash entries (spread cells have no blockId)
export const ER_LEVEL_FLASH_ID = -1;

/**
 * Derives the blockId used for flash matching from AG Grid params.
 * Shared across cell renderers (Quote, Cash, ReferencePriceLeg).
 */
export function getBlockIdForFlash(
  isLeg: boolean,
  isER: boolean,
  isOutright: boolean,
  params: any,
): number | null {
  if (isLeg) return params.data?.blockId ?? null;
  if (isER && isOutright) return params.data?.legs?.[0]?.blockId ?? null;
  if (isER) return ER_LEVEL_FLASH_ID;
  return null;
}

function isValueMissing(value: any): boolean {
  return value == null || value === undefined || value === "";
}

/**
 * Works with the tradeJSONQuoted format (has .blocks[] and .esid) used by the validation functions.
 * Returns one flash entry per missing column — "bid" or "ask" — so the renderer
 * can do a simple match on `cell.quoteSide === params.colDef.field`.
 */
export function returnMissingQuoteCellsFromTradeJSON(
  tradeJSONQuoted: any,
): ILegCellToFlash[] {
  const cells: ILegCellToFlash[] = [];
  if (!tradeJSONQuoted?.blocks) return cells;

  tradeJSONQuoted.blocks.forEach((block: any) => {
    if (block.blockId == null) return;

    if (block.quoteSide == QuoteSide.Bid && isValueMissing(block.bid)) {
      cells.push({
        esid: tradeJSONQuoted.esid,
        blockId: block.blockId,
        field: "quote",
        quoteSide: "bid",
      });
    }
    if (block.quoteSide == QuoteSide.Ask && isValueMissing(block.ask)) {
      cells.push({
        esid: tradeJSONQuoted.esid,
        blockId: block.blockId,
        field: "quote",
        quoteSide: "ask",
      });
    }
  });

  return cells;
}

/**
 * Two-Way variant: checks BOTH bid and ask on every block regardless of quoteSide.
 * Pushes a separate entry for each missing column.
 */
export function returnMissingTwoWayQuoteCellsFromTradeJSON(
  tradeJSONQuoted: any,
): ILegCellToFlash[] {
  const cells: ILegCellToFlash[] = [];
  if (!tradeJSONQuoted?.blocks) return cells;

  tradeJSONQuoted.blocks.forEach((block: any) => {
    if (block.blockId == null) return;

    if (isValueMissing(block.bid)) {
      cells.push({
        esid: tradeJSONQuoted.esid,
        blockId: block.blockId,
        field: "quote",
        quoteSide: "bid",
      });
    }
    if (isValueMissing(block.ask)) {
      cells.push({
        esid: tradeJSONQuoted.esid,
        blockId: block.blockId,
        field: "quote",
        quoteSide: "ask",
      });
    }
  });

  return cells;
}

/**
 * Works with ExecutionStrategy.legs format — used for execute validation
 * where checkAllLegsAreQuotedDetailedResult operates on `data.legs`.
 */
export function returnMissingQuoteCellsFromLegs(
  data: ExecutionStrategy,
  excludeUnwindTypes?: string[],
): ILegCellToFlash[] {
  const cells: ILegCellToFlash[] = [];
  if (!data.legs) return cells;

  data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
    if (leg.blockId == null) return;
    if (excludeUnwindTypes && excludeUnwindTypes.includes(leg.unwind as string))
      return;

    if (leg.quoteSide == QuoteSide.Bid && isValueMissing(leg.bid)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "bid",
      });
    }
    if (leg.quoteSide == QuoteSide.Ask && isValueMissing(leg.ask)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "ask",
      });
    }
  });

  return cells;
}

/**
 * Returns ILegCellToFlash[] for NPV/Recoupon blocks missing their transactionFee.
 * Only checks blocks where unwind is "NPV" or "Recoupon" (or all blocks when data.unwind is true).
 */
export function returnBlocksWithMissingCash(
  data: ExecutionStrategy,
): ILegCellToFlash[] {
  const cellsToFlash: ILegCellToFlash[] = [];

  if (!data.legs) return cellsToFlash;

  data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
    if (leg.blockId == null) return;

    const isNPVOrRecoupon =
      data.unwind === true ||
      leg.unwind === "NPV" ||
      leg.unwind === "Recoupon";

    if (!isNPVOrRecoupon) return;

    if (!leg.fees || leg.fees.length === 0) {
      cellsToFlash.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "cash",
      });
      return;
    }

    const feeObject = leg.fees.find(
      (feeObj) => feeObj.transactionFeeSide == leg.quoteSide,
    );

    if (!feeObject || feeObject.transactionFee == null) {
      cellsToFlash.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "cash",
      });
    }
  });

  return cellsToFlash;
}

function isQuotedAtZero(value: any): boolean {
  // Catches "0", "0-00", "-0", "-0-00", "000", "0.00" etc. so the zero-quote validation
  // owns all zero forms, including user-entered "-0" which is numerically zero despite
  // the leading minus.
  return isNumericallyZero(value);
}

function isQuotedNegative(value: any): boolean {
  if (value == null || value === "") return false;
  // "-0" / "-0-00" are numerically zero, not negative — zero validation handles them.
  if (isNumericallyZero(value)) return false;
  return String(value).trim().startsWith("-");
}

function isPriceNotation(notation: string | undefined): boolean {
  return notation === "$p" || notation === "$fr";
}

/**
 * Returns flash entries for legs with $p or $fr notation that have bid/ask quoted at zero.
 * LQC-590: Price notation blocks cannot be quoted at 0.
 * Checks leg.notation first, falls back to data.notationER / data.notationES for outrights
 * where leg-level notation may differ from the trade's actual notation.
 */
export function returnPriceNotationBlocksQuotedAtZero(
  data: ExecutionStrategy,
): ILegCellToFlash[] {
  const cells: ILegCellToFlash[] = [];
  if (!data.legs) return cells;

  data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
    if (leg.blockId == null) return;
    const notation = leg.notation || data.notationER || data.notationES;
    if (!isPriceNotation(notation)) return;

    if (isQuotedAtZero(leg.bid)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "bid",
      });
    }
    if (isQuotedAtZero(leg.ask)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "ask",
      });
    }
  });

  return cells;
}

/**
 * Returns flash entries for legs with $p or $fr notation that have bid/ask quoted at a negative value.
 * LQC-889: Price notation blocks cannot be quoted negative (mirrors LQC-590 zero-quote handling).
 */
export function returnPriceNotationBlocksQuotedNegative(
  data: ExecutionStrategy,
): ILegCellToFlash[] {
  const cells: ILegCellToFlash[] = [];
  if (!data.legs) return cells;

  data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
    if (leg.blockId == null) return;
    const notation = leg.notation || data.notationER || data.notationES;
    if (!isPriceNotation(notation)) return;

    if (isQuotedNegative(leg.bid)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "bid",
      });
    }
    if (isQuotedNegative(leg.ask)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "ask",
      });
    }
  });

  return cells;
}

/**
 * Returns flash entries for FUT instrument type legs (any notation) that have bid/ask quoted at zero.
 * Existing FUT zero-quote check (14651) moved here to add flashing support.
 */
export function returnFUTBlocksQuotedAtZero(
  data: ExecutionStrategy,
): ILegCellToFlash[] {
  const cells: ILegCellToFlash[] = [];
  if (!data.legs) return cells;

  data.legs.forEach((leg: ExecutionRequestQuoteLeg) => {
    if (leg.blockId == null) return;
    if (leg.instrumentType !== InstrumentTypeCode.FUT) return;

    if (isQuotedAtZero(leg.bid)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "bid",
      });
    }
    if (isQuotedAtZero(leg.ask)) {
      cells.push({
        esid: data.esid,
        blockId: leg.blockId,
        field: "quote",
        quoteSide: "ask",
      });
    }
  });

  return cells;
}

/**
 * Triggers flash on the given cells for 3 seconds then clears.
 * Expands the row so flashing cells are visible.
 */
export function triggerValidationFlash(cellsToFlash: ILegCellToFlash[]): void {
  if (cellsToFlash.length === 0) return;

  tradeSettingsStore.getState().updateLegCellsToFlash(cellsToFlash);

  const uniqueEsids = [...new Set(cellsToFlash.map((c) => c.esid).filter(Boolean))];
  uniqueEsids.forEach((esid) => tradeDataStore.getState().updateEsidsToExpand(esid));

  setTimeout(() => {
    tradeSettingsStore.getState().clearLegCellsToFlash();
  }, agGridFlashNotifications.getState().validationFlashDuration);
}
